// TODO: AI PROJECT UI 수정 및 API 연동 필요 by.BLO

import { css } from '@emotion/react';
import {
  Button,
  Card,
  CheckBox,
  color,
  FormGroup,
  Icon,
  Input,
  MediaCard,
  Modal,
  spacing,
  textStyleCaption,
  textStyleSubHeading,
  typography,
  Typography,
  ToggleButton,
  ButtonGroup
} from '@uniquegood/realworld-studio-design';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { Controller, useForm } from 'react-hook-form';

import DOMPurify from 'dompurify';
import { AxiosError } from 'axios';
import { coreApi } from '@/api';

import {
  useDidUpdateEffect,
  useIsMobile,
  useMission,
  useModalState,
  usePlanLimits,
  usePromotionModal,
  useRequest,
  useSaveLimitCheck
} from '@/hooks';
import { useUnsavedChanges } from '@/providers/UnsavedChanges';
import {
  invalidFieldBoilerplate,
  toast,
  hasCompletedAiCreditOnboarding,
  setAiCreditOnboardingCompleted
} from '@/utils';

import HeaderSwitchTab, { HeaderSwitchTabProps } from '@/components/HeaderSwitchTab';

import {
  CardItemGap,
  DisplayBlock,
  Flex,
  JustifySpaceBetween,
  MarginBottom10,
  MarginLeft24,
  MarginBottom16,
  MarginRight16,
  MarginTop10,
  TextAlignCenter,
  textShortening,
  Identity,
  textShorteningFromTwoLine,
  ATagStyle,
  DisplayNone
} from '@/styles';
import { onMobile } from '@/styles/responsive';
import Uploader from '@/components/Uploader';
import BottomFloatingBar, {
  BOTTOM_FLOATING_BAR_HEIGHT,
  SpacingForBottomFloatingBar
} from '@/components/BottomFloatingBar';
import Editor from '@/components/Editor';
import ContainerHeader from '@/components/ContainerHeader';
import { BottomFloatingBarExtendStyle } from '@/components/ActionHandler/styles';
import PhoneMockUp from '../PhoneMockUp';
import { tooltipImage } from '../../assets';
import { decodeHtml, MISSION_API_VERSION_HEADER } from '../../utils';
import { defaultCoverImage } from '../../const';
import { checkboxGroupLayout } from '../../styles';
import RouteModal from '@/components/RouteModal';
import {
  InputEventName,
  PageViewEventName,
  SaveEventName,
  track,
  PromotionTriggerLocation
} from '@/track';
import AiCreditOnboardingTemplate from '@/components/AiCreditOnboardingTemplate';

type FormData = Omit<Mission, 'coverImageId' | 'coverImageUrl' | 'quests'> & {
  coverImage?: [UserFileResponseModel];
  isVisibleDefault?: boolean;
};

function transformToFormData(mission: Mission): FormData {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { quests, coverImageId, coverImageUrl, shortDescription, ...rest } = mission;

  return {
    ...rest,
    shortDescription: decodeHtml(shortDescription),
    forceChangeIsLocked: false,
    forceChangeIsVisible: false,
    // Hack: 미션 변경 시 videoUrl 값이 undefined 인 경우 해당 필드 초기화가 안되어서 빈 문자열을 할당함
    videoUrl: rest.videoUrl || '',
    coverImage:
      !!coverImageId && !!coverImageUrl ? [{ id: coverImageId, url: coverImageUrl }] : undefined
  };
}

const VISIBLE_MISSION_REQUIRED_ERROR = 'VisibleMissionRequired';

export default function EditMissionPage() {
  const { appId, projectId, scenarioId, missionId } = useParams<AppParam>();
  const isMobile = useIsMobile();

  const endpoint = {
    get: `/apps/${appId}/scenarios/${scenarioId}/missions`,
    put: `/apps/${appId}/missions/${missionId}`
  };

  const { missions, missionIndex, mission, mutate } = useMission();

  const { control, handleSubmit, watch, reset, formState, setValue } = useForm<FormData>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    defaultValues: mission && transformToFormData(mission)
  });

  const history = useHistory();

  const { modal, openModal, closeModal } = useModalState();
  const { checkSaveLimitAndExecute, modal: saveLimitModal, isOverLimit } = useSaveLimitCheck();
  const { openPromotionModal } = usePromotionModal();
  const { plan, hasReached } = usePlanLimits();
  const { registerSaveHandler } = useUnsavedChanges();

  const [isDeleting, setIsDeleting] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const [selectedContentHeaderTab, setSelectedContentHeaderTab] =
    useState<ContentCardHeaderTab>('editor');

  const { data: themeResponse } = useRequest<CommonResponseModel<ProjectThemeResponseModel>>(
    `/apps/${appId}/projects/${projectId}/theme`,
    undefined,
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );

  const theme = (themeResponse?.data.theme.toLowerCase() as ProjectThemeEnum) ?? 'light';

  const parentPath = `/apps/${appId}/projects/${projectId}/scenarios/${scenarioId}`;

  useEffect(() => {
    track.onPageView({ pageViewEventName: PageViewEventName.view_editgame_editmission });

    // 온보딩 쿠키 체크
    if (!hasCompletedAiCreditOnboarding()) {
      setShowOnboarding(true);
    }
  }, []);

  useEffect(() => {
    if (mission) {
      reset(transformToFormData(mission));
    }
  }, [mission]);

  useEffect(() => {
    if (mission) {
      sessionStorage.setItem('missionSessionData', JSON.stringify(mission));
    }
  }, [mission]);

  const missionUpdateCount = useRef(0);

  useDidUpdateEffect(() => {
    missionUpdateCount.current = 0;
  }, [missionId]);

  useEffect(() => {
    if (mission) {
      missionUpdateCount.current += 1;

      const shouldUpdateTab = missionUpdateCount.current === 1;
      if (shouldUpdateTab) {
        setSelectedContentHeaderTab(mission.videoUrl ? 'video' : 'editor');
      }
    }
  }, [mission]);

  // (무료) 유료 기능 포함 콘텐츠 수정 시도 시
  const displayContentNotAllowedModal = () => {
    openModal({
      title: '수정 불가 팝업 노출',
      children: (
        <>
          <Typography
            type="body"
            cssStyle={[
              MarginBottom10,
              css`
                white-space: pre-line;
              `
            ]}
          >
            {`무료 요금제에서 제한된 기능이 포함되어 있어 콘텐츠 수정이 불가해요. \n아래 중 하나를 선택하여 진행해 주세요.`}
          </Typography>
          <Typography type="body">1. 요금제 업그레이드</Typography>
          <Typography type="body">2. [미션 &gt; 퀘스트]에서 사용 불가능한 기능 제거</Typography>
          <Typography
            type="body"
            textColor="critical"
            cssStyle={css`
              margin-top: ${spacing.margin.large};
              margin-bottom: ${spacing.margin.small};
              font-weight: ${typography.weight.medium};
            `}
          >
            무료 요금제 제한 기능
          </Typography>
          <ul
            css={css`
              list-style: disc;
              margin-left: 20px;
            `}
          >
            <li>
              <Typography as="span" type="body">
                디바이스 연동 도구 (NFC 태그)
              </Typography>
            </li>
            <li>
              <Typography as="span" type="body">
                카메라 기반 실감 기술 도구 (프레임 촬영, QR, AR, 컬러 렌즈, 오버랩)
              </Typography>
            </li>
            <li>
              <Typography as="span" type="body">
                위치 기반 상호작용 도구를 사용한 경우 (GPS 특정 장소 이동)
              </Typography>
            </li>
            <li>
              <Typography as="span" type="body">
                아이템 - 5개 초과
              </Typography>
            </li>
          </ul>
        </>
      ),
      primaryAction: {
        content: '확인',
        onAction: closeModal
      },
      secondaryAction: {
        content: '요금제 업그레이드',
        onAction: () => {
          closeModal();
          openPromotionModal({ triggerLocation: PromotionTriggerLocation.mission_edit_banner });
        }
      }
    });
  };

  const displayLimitExceededModal = (plan: PlanType) => {
    let content;

    if (plan === 'Free') {
      content =
        '요금제가 무료로 전환되어 콘텐츠 수정사항을 저장할 수 없습니다. 콘텐츠를 3개 이하로 줄이거나 요금제를 업그레이드해 주세요.';
    } else if (plan === 'Pro') {
      content =
        '요금제가 프로로 전환되어 콘텐츠 수정사항을 저장할 수 없습니다. 콘텐츠를 10개 이하로 줄이거나 요금제를 업그레이드해 주세요.';
    }

    // content가 없으면 모달을 표시하지 않음
    if (!content) {
      return;
    }

    openModal({
      title: '수정사항을 저장할 수 없어요',
      children: <Typography type="body">{content}</Typography>,
      primaryAction: {
        content: '확인',
        onAction: closeModal
      },
      secondaryAction: {
        content: '요금제 업그레이드',
        onAction: () => {
          closeModal();
          openPromotionModal({
            triggerLocation: PromotionTriggerLocation.content_save_blocked_content_limit
          });
        }
      }
    });
  };

  const displayVisibleMissionRequiredModal = () => {
    openModal({
      title: '수정사항을 저장할 수 없어요',
      cssStyle: TextAlignCenter,
      children: (
        <>
          <Icon
            cssStyle={MarginBottom10}
            icon="times_circle_regular"
            size="100px"
            color={color.interactive_critical_default}
          />
          최소 1개의 미션은 기본 표시 여부가 &apos;표시&apos;여야 해요
        </>
      ),
      primaryAction: {
        content: '확인',
        onAction: closeModal
      }
    });
  };

  const hasVisibleMissionAfterSave = React.useCallback(
    (nextIsVisibleDefault?: boolean) => {
      if (!missions || missions.length === 0) {
        return false;
      }

      return missions.some((currentMission) => {
        if (currentMission.id.toString() === missionId) {
          return Boolean(nextIsVisibleDefault);
        }

        return currentMission.isVisibleDefault;
      });
    },
    [missions, missionId]
  );

  const { data: projects } = useRequest<CommonResponseModel<Project[]>>(
    `/apps/${appId}/projects`,
    {},
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );
  const projectCount = projects?.data?.length ?? 0;

  // 실제 저장 로직 (자동 저장용)
  const performSave = React.useCallback(
    async (formData: FormData) => {
      // 미션 저장
      const { coverImage, ...data } = formData;

      if (!hasVisibleMissionAfterSave(data.isVisibleDefault)) {
        displayVisibleMissionRequiredModal();
        throw new Error(VISIBLE_MISSION_REQUIRED_ERROR);
      }

      // Free, Pro 요금제에서 콘텐츠의 개수가 제한을 초과한 경우 모달 표시
      const isProjectLimitReached = hasReached('projects', projectCount);
      if (isProjectLimitReached && (plan === 'Free' || plan === 'Pro')) {
        displayLimitExceededModal(plan);
        throw new Error('NotAllowedPlan');
      }

      try {
        await coreApi.put<Mission>(endpoint.put, {
          ...data,
          videoUrl: data.videoUrl || null,
          description: data.description || '<p></p>',
          coverImageId: coverImage?.[0]?.id
        }, MISSION_API_VERSION_HEADER);

        track.onSave({ saveEventName: SaveEventName.save_editgame_mission });
        await mutate();
      } catch (error) {
        const errorTitle = (error as AxiosError<{ errorTitle: string }>)?.response?.data.errorTitle;
        if (errorTitle === 'NotAllowedPlan') {
          displayContentNotAllowedModal();
          throw error;
        }
        throw error;
      }
    },
    [
      endpoint.put,
      mutate,
      displayContentNotAllowedModal,
      displayLimitExceededModal,
      displayVisibleMissionRequiredModal,
      hasVisibleMissionAfterSave,
      hasReached,
      mission,
      plan,
      projectCount
    ]
  );

  const executeSubmit = React.useCallback(
    async ({ coverImage, ...data }: FormData): Promise<boolean> => {
      // 콘텐츠 개수 제한 체크
      if (isOverLimit) {
        checkSaveLimitAndExecute(() => undefined);
        return false;
      }

      if (data.forceChangeIsVisible || data.forceChangeIsLocked) {
        openModal({
          title: '전체 강제 적용이 포함되어 있어요',
          children: (
            <>
              <Typography as="p" type="body" cssStyle={MarginBottom16}>
                전체 강제 적용이 앱에 반영되려면 시간이 걸려요.
                <br />
                정말 전체 강제 적용을 포함해서 저장하시겠어요?
              </Typography>
              <Typography as="p" type="body">
                (누적 플레이 인원 기준 1,000명당 대략 1분씩 소요)
              </Typography>
            </>
          ),
          primaryAction: {
            icon: 'save_solid',
            content: '저장',
            onAction: async () => {
              try {
                await performSave({ coverImage, ...data });
                toast({ message: '저장 완료!' });
                closeModal();
              } catch {
                // 저장 차단/실패 시에는 현재 화면에 머문다.
              }
            },
            loading: formState.isSubmitting
          },
          secondaryAction: {
            content: '취소',
            onAction: closeModal
          }
        });
        return false;
      }

      try {
        await performSave({ coverImage, ...data });
        toast({ message: '저장 완료!' });
        return true;
      } catch (error) {
        if ((error as Error).message === VISIBLE_MISSION_REQUIRED_ERROR) {
          return false;
        }

        return false;
      }
    },
    [
      checkSaveLimitAndExecute,
      closeModal,
      formState.isSubmitting,
      isOverLimit,
      openModal,
      performSave
    ]
  );

  const onSubmit = React.useCallback(async (): Promise<boolean> => {
    let isSaved = false;

    await handleSubmit(
      async (data) => {
        isSaved = await executeSubmit(data);
      },
      (errors) => {
        const requiredFieldsToLocalString: Record<string, string> = {
          name: '미션 이름'
        };

        const requiredFields = Object.entries(errors)
          .filter(([, error]) => error && 'type' in error && error.type === 'required')
          .map(([field]) => requiredFieldsToLocalString[field]);

        if (requiredFields.length > 0) {
          openModal({
            title: '필수 항목을 모두 채워주세요',
            cssStyle: TextAlignCenter,
            children: (
              <>
                <Icon
                  cssStyle={MarginBottom10}
                  icon="times_circle_regular"
                  size="100px"
                  color={color.interactive_critical_default}
                />
                {requiredFields.join(', ')}이(가) 비어있어요
              </>
            ),
            primaryAction: {
              content: '확인',
              onAction: closeModal
            }
          });
        }
      }
    )();

    return isSaved;
  }, [closeModal, executeSubmit, handleSubmit, openModal]);

  async function onDelete() {
    openModal({
      title: '미션을 삭제하시겠어요?',
      size: 'small',
      children: '삭제한 미션은 다시 복구할 수 없어요',
      primaryAction: {
        content: '삭제',
        icon: 'trash_alt_solid',
        type: 'destructive',
        onAction: async () => {
          setIsDeleting(true);
          try {
            await coreApi.delete(`/apps/${appId}/missions/${missionId}`, MISSION_API_VERSION_HEADER);
            await mutate();

            history.push(
              missions && missionIndex
                ? `${parentPath}/missions/${missions[missionIndex - 1].id}`
                : parentPath
            );
            toast({ message: '미션을 삭제했어요' });
          } finally {
            setIsDeleting(false);
            closeModal();
          }
        }
      },
      secondaryAction: {
        content: '취소',
        onAction: closeModal
      }
    });
  }

  const handleOnboardingComplete = () => {
    setAiCreditOnboardingCompleted();
    setShowOnboarding(false);
  };

  const editorBackgroundUrl =
    themeResponse?.data.backgroundImage?.fileUrl ?? defaultCoverImage[theme];

  const watchFormData = watch();

  // 저장 가능 여부 확인 (요금제 제한 체크)
  const canSave = React.useCallback(() => {
    // Free, Pro 요금제에서 콘텐츠의 개수가 제한을 초과한 경우 저장 불가
    const isProjectLimitReached = hasReached('projects', projectCount);
    if (isProjectLimitReached && (plan === 'Free' || plan === 'Pro')) {
      return false;
    }

    return true;
  }, [hasReached, projectCount, plan]);

  // 전역 저장 핸들러 등록
  useEffect(() => {
    const unregister = registerSaveHandler(
      async () => {
        // 자동 저장 시에는 모달 없이 바로 저장
        // handleSubmit을 사용하여 유효성 검사를 통과한 데이터만 저장
        return new Promise<void>((resolve, reject) => {
          handleSubmit(
            async (formData) => {
              try {
                // forceChangeIsVisible이나 forceChangeIsLocked가 있어도 자동 저장은 진행
                await performSave(formData);
                resolve();
              } catch (error) {
                reject(error);
              }
            },
            () => {
              // 유효성 검사 실패 시에도 저장 시도 (자동 저장이므로)
              resolve();
            }
          )();
        });
      },
      () => formState.isDirty,
      canSave
    );

    return unregister;
  }, [registerSaveHandler, formState.isDirty, handleSubmit, performSave, canSave]);

  const EditorStyle = css`
    height: fit-content;

    > div:nth-of-type(2) .ql-container {
      margin-bottom: ${BOTTOM_FLOATING_BAR_HEIGHT};
      color: ${theme === 'dark' ? 'white' : 'black'};
      height: 100%;
    }

    > div:nth-of-type(2) .ql-editor {
      height: ${themeResponse?.data.backgroundImage?.fileUrl ? '100vh' : 'auto'};
      min-height: 300px;
      color: ${theme === 'dark' ? 'white' : 'black'};
      background-image: url(${editorBackgroundUrl});
      background-size: cover;
      background-position: center;
      object-fit: cover;
      display: flex;
      flex-direction: column;
      overflow: auto;
    }
  `;

  const showEmbedVideo = youtubeUrlPattern.test(watchFormData.videoUrl ?? '');

  return (
    <section css={ScrollLayout}>
      <RouteModal
        when={formState.isDirty && canSave()}
        isValid={formState.isValid}
        loading={formState.isSubmitting}
        onSubmit={onSubmit}
      />
      <ContainerHeader
        cssStyle={[
          MarginLeft24,
          MarginBottom16,
          watchFormData.name ? Identity : HeaderTitleEmptyStateStyle
        ]}
      >
        {watchFormData.name}
      </ContainerHeader>
      <form
        css={[FormLayout, SpacingForBottomFloatingBar]}
        id="form"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit().catch(() => undefined);
        }}
      >
        <SettingPreview
          name={watchFormData.name}
          shortDescription={watchFormData.shortDescription}
          coverImageUrl={watchFormData.coverImage?.[0]?.url}
        />
        <Card
          header={{ prefix: <Icon icon="info_circle_solid" />, content: '미션 설정' }}
          cssStyle={[CardItemGap, CardHeaderStyle]}
        >
          <Controller
            control={control}
            name="name"
            rules={{ required: true }}
            render={({ field: { value, onChange }, fieldState: { invalid } }) => (
              <FormGroup
                label="미션 이름"
                id="name"
                requiredIndicator
                errorText={invalidFieldBoilerplate(invalid)}
              >
                <Input
                  id="name"
                  value={value ?? ''}
                  onChange={onChange}
                  onKeyPress={preventSubmit}
                />
              </FormGroup>
            )}
          />
          <Controller
            control={control}
            name="shortDescription"
            render={({ field: { value, onChange } }) => (
              <FormGroup label="한 줄 설명" id="shortDescription">
                <Input
                  id="shortDescription"
                  value={value ?? ''}
                  onChange={onChange}
                  onKeyPress={preventSubmit}
                />
              </FormGroup>
            )}
          />
          <Controller
            control={control}
            name="coverImage"
            render={({ field: { value, onChange }, fieldState: { invalid } }) => {
              const memoedFiles = useMemo(() => value, [value?.[0]?.id]);
              return (
                <FormGroup
                  id="uploader"
                  label="미션 카드 이미지"
                  labelTooltip={{
                    content: (
                      <>
                        미션 카드 이미지는 모바일 기기의
                        <br /> 해상도에 맞는 크기와 비율로 자동
                        <br /> 변환되어 보여져요
                      </>
                    )
                  }}
                  errorText={invalidFieldBoilerplate(invalid)}
                >
                  <Uploader
                    key={missionId}
                    id="uploader"
                    hasSearch
                    imageUseForType="mission-card-image"
                    aiImageRatio="9:16"
                    uploadedFiles={memoedFiles}
                    getUploadFiles={onChange}
                    ratio="vertical"
                    useFor="mission-cover"
                    isAiProject
                    isVerticalLayout={isMobile}
                  />
                </FormGroup>
              );
            }}
          />
          <div role="group" css={[Flex, JustifySpaceBetween, GroupLayout]}>
            <FormGroup
              label="기본 표시 여부"
              cssStyle={checkboxGroupLayout}
              labelTooltip={{
                content: (
                  <>
                    미션 카드 화면(하단 이미지)에서 기본 표시 여부
                    <br />를 결정해요. 퀘스트 편집에서 표시 여부를 변경
                    <br />할 수 있어요.
                    <br />
                    <br />
                    변경한 상태를 되돌리려면 &apos;전체 강제 적용&apos;을 체<br />
                    크하고 다시 저장해주세요.{' '}
                    <a href={helpLink} target="_blank" rel="noreferrer" css={ATagStyle}>
                      자세히보기
                    </a>
                    <img
                      src={tooltipImage.display}
                      alt="미션 카드 표시 예시"
                      width="273px"
                      css={[DisplayBlock, MarginTop10]}
                    />
                  </>
                ),
                deactivateOnBlur: false,
                preferredPosition: 'below'
              }}
            >
              <Controller
                control={control}
                name="isVisibleDefault"
                defaultValue
                render={({ field: { value, onChange } }) => (
                  <ToggleButton checked={value} onChange={onChange} cssStyle={MarginRight16}>
                    {value ? '표시' : '미표시'}
                  </ToggleButton>
                )}
              />
              <CheckBox
                checked={watchFormData.forceChangeIsVisible}
                onChange={(event) =>
                  setValue('forceChangeIsVisible', event.target.checked, { shouldDirty: false })
                }
              >
                전체 강제 적용
              </CheckBox>
            </FormGroup>
            <FormGroup
              label="기본 잠금 여부"
              cssStyle={checkboxGroupLayout}
              labelTooltip={{
                content: (
                  <>
                    미션 카드 화면(하단 이미지)에서 기본 잠금 여부
                    <br />를 결정해요. 퀘스트 편집에서 잠금 여부를 변경
                    <br />할 수 있어요.
                    <br />
                    <br />
                    변경한 상태를 되돌리려면 &apos;전체 강제 적용&apos;을 체<br />
                    크하고 다시 저장해주세요.{' '}
                    <a href={helpLink} target="_blank" rel="noreferrer" css={ATagStyle}>
                      자세히보기
                    </a>
                    <img
                      src={tooltipImage.lock}
                      alt="잠금된 미션 카드 예시"
                      width="273px"
                      css={[DisplayBlock, MarginTop10]}
                    />
                  </>
                ),
                deactivateOnBlur: false
              }}
            >
              <Controller
                control={control}
                name="isLockedDefault"
                render={({ field: { value, onChange } }) => (
                  <ToggleButton checked={value} onChange={onChange} cssStyle={MarginRight16}>
                    {value ? '잠금' : '미잠금'}
                  </ToggleButton>
                )}
              />
              <CheckBox
                checked={watchFormData.forceChangeIsLocked}
                onChange={(event) =>
                  setValue('forceChangeIsLocked', event.target.checked, { shouldDirty: false })
                }
              >
                전체 강제 적용
              </CheckBox>
            </FormGroup>
          </div>
        </Card>
        <ContentPreview name={mission?.name ?? ''} description={mission?.description} />
        <Card
          header={{
            prefix: <Icon icon="clipboard_solid" />,
            content: (
              <>
                <Typography as="span" type="button" cssStyle={MarginRight16}>
                  미션 내용
                </Typography>
                <HeaderSwitchTab<ContentCardHeaderTab>
                  tabs={contentCardHeaderTabs}
                  selectedTab={selectedContentHeaderTab}
                  setSelectedTab={setSelectedContentHeaderTab}
                />
              </>
            )
          }}
          cssStyle={[CardHeaderStyle, EditorStyle, cardHeaderTabsContentStyle]}
        >
          <div css={{ display: selectedContentHeaderTab === 'video' ? 'none' : 'initial' }}>
            <Controller
              control={control}
              name="description"
              render={({ field: { value, onChange } }) => (
                <Editor
                  key={missionId}
                  css={textEditorHeight}
                  defaultValue={mission?.description}
                  value={value}
                  onChange={(nextValue) => {
                    onChange(nextValue);
                    track.onInputThrottled({
                      inputEventName: InputEventName.input_editgame_mission_missiondetail
                    });
                  }}
                  minHeight="367px"
                />
              )}
            />
          </div>
          <div
            css={[
              { display: selectedContentHeaderTab === 'editor' ? 'none' : 'initial' },
              youtubeUrlFormStyle
            ]}
          >
            <Controller
              control={control}
              name="videoUrl"
              rules={{ pattern: youtubeUrlPattern }}
              render={({ field, fieldState }) => (
                <FormGroup
                  label="유튜브 동영상 주소"
                  cssStyle={youtubeUrlInputFormGroupStyle}
                  errorText={
                    fieldState.error?.type === 'pattern'
                      ? '유튜브 주소 형식에 맞지 않아요'
                      : undefined
                  }
                >
                  <Input {...field} placeholder="예) https://www.youtube.com/watch?v=Sample" />
                  {showEmbedVideo && (
                    <iframe
                      css={youtubeEmbedVideoStyle}
                      style={{
                        aspectRatio:
                          watchFormData.videoRatioType === 'Vertical' ? '9 / 16' : undefined
                      }}
                      width="300"
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      src={convertToEmbedYoutubeUrl(watchFormData.videoUrl!)}
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  )}
                </FormGroup>
              )}
            />
            <Controller
              control={control}
              name="videoRatioType"
              render={({ field: { value, onChange } }) => (
                <FormGroup label="화면비" cssStyle={!showEmbedVideo ? DisplayNone : undefined}>
                  <ButtonGroup segmented>
                    <Button
                      type={value === 'Horizontal' ? 'primary' : 'outlinePrimary'}
                      onClick={() => onChange('Horizontal')}
                    >
                      가로
                    </Button>
                    <Button
                      type={value === 'Vertical' ? 'primary' : 'outlinePrimary'}
                      onClick={() => onChange('Vertical')}
                    >
                      세로
                    </Button>
                  </ButtonGroup>
                </FormGroup>
              )}
            />
          </div>
        </Card>
      </form>
      <BottomFloatingBar
        form="form"
        cssStyle={[
          BottomFloatingBarExtendStyle({ leftElementWidth: '306px' }),
          MissionBottomFloatingBarStyle
        ]}
        loading={formState.isSubmitting}
      >
        <Button type="plainDestructive" onClick={onDelete}>
          미션 삭제
        </Button>
      </BottomFloatingBar>
      <Modal {...modal} primaryAction={{ ...modal.primaryAction, loading: isDeleting }} />
      <Modal {...saveLimitModal} />

      {/* AI Credit Onboarding - 모바일에서는 숨김 */}
      {!isMobile && (
        <AiCreditOnboardingTemplate
          isVisible={showOnboarding}
          onComplete={handleOnboardingComplete}
        />
      )}
    </section>
  );
}

function convertToEmbedYoutubeUrl(url: string) {
  const execArray = youtubeUrlPattern.exec(url);
  if (!execArray) throw Error('유튜브 임베드 URL 형식으로 변환할 수 없는 주소입니다');

  const videoId = execArray[6];
  return `https://www.youtube.com/embed/${videoId}`;
}

const youtubeUrlPattern =
  /https:\/\/(www\.)?(youtube.com\/shorts\/|youtu\.be\/|youtube\.com\/(watch\?(.*&)?v=|(embed|v)\/))([^?&"'>]+)/;

type ContentCardHeaderTab = 'editor' | 'video';

const contentCardHeaderTabs: HeaderSwitchTabProps<ContentCardHeaderTab>['tabs'] = [
  { value: 'editor', label: '편집기 사용' },
  { value: 'video', label: '유튜브 영상' }
];

const cardHeaderTabsContentStyle = css`
  > div:first-of-type {
    background-color: ${color.surface_default_subdued};
    padding-bottom: 0;
    > span {
      margin-bottom: ${spacing.margin.large};
    }
    > div > span {
      display: flex;
    }
  }
`;

const MissionBottomFloatingBarStyle = css`
  box-shadow: 0 -1px 0 ${color.border_default_subdued};
`;

const youtubeUrlFormStyle = css`
  > *:not(:last-of-type) {
    margin-bottom: ${spacing.margin.small};
  }
`;

const youtubeUrlInputFormGroupStyle = css`
  > :nth-of-type(2) {
    display: flex;
    flex-direction: column;
    gap: ${spacing.margin.xsmall};
  }
`;

const youtubeEmbedVideoStyle = css`
  border-radius: ${spacing.borderRadius.small};
`;

const HeaderTitleEmptyStateStyle = css`
  h1::before {
    content: '_';
    visibility: hidden;
  }
`;

const FormLayout = css`
  padding: 0 ${spacing.margin.xlarge2};

  display: grid;
  gap: ${spacing.margin.xlarge2};

  justify-content: start;

  grid-template-columns: auto minmax(0, 650px);

  ${onMobile} {
    grid-template-columns: minmax(0, 100%);
  }
`;

const CardHeaderStyle = css`
  > div:first-of-type {
    background-color: #f2f9ff;
  }
`;

function preventSubmit(event: React.KeyboardEvent) {
  if (event.key === 'Enter') {
    event.preventDefault();
  }
}

type SettingPreviewProps = Pick<Mission, 'name'> &
  Partial<Pick<Mission, 'name' | 'shortDescription' | 'coverImageUrl'>>;

function SettingPreview({
  name = '',
  shortDescription,
  coverImageUrl = defaultCoverImage.card
}: SettingPreviewProps) {
  return (
    <PhoneMockUp type="setting" cssStyle={[SettingPreviewLayout, hideOnMobile]}>
      <MediaCard
        source={coverImageUrl}
        title={name}
        summary={shortDescription}
        css={[MediaCardStyle, name.length === 0 ? MediaCardTitleEmptyState : Identity]}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        titleStyle={textStyleSubHeading}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        summaryStyle={[textStyleCaption, textShorteningFromTwoLine]}
      />
    </PhoneMockUp>
  );
}

const hideOnMobile = css`
  ${onMobile} {
    display: none;
  }
`;

const SettingPreviewLayout = css`
  padding: 0 ${spacing.padding.xlarge3};

  && {
    padding-top: 94px;
  }
`;

const MediaCardStyle = css`
  && {
    height: 290px;
    border: none;
  }

  dl {
    padding: ${spacing.margin.small};
    line-height: ${spacing.common.medium};
    font-size: 12px !important;
  }

  dd {
    margin-top: ${spacing.margin.small};

    ${textShortening}

    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const MediaCardTitleEmptyState = css`
  dt {
    content: '.';
    visibility: hidden;
  }
`;

type ContentPreviewProps = Pick<Mission, 'name'> & Partial<Pick<Mission, 'description'>>;

function ContentPreview({ name, description }: ContentPreviewProps) {
  const { appId, projectId } = useParams<AppParam>();
  const { data: themeResponse } = useRequest<CommonResponseModel<ProjectThemeResponseModel>>(
    `/apps/${appId}/projects/${projectId}/theme`,
    undefined,
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );
  const theme = themeResponse?.data.theme ?? 'light';

  const ScrollbarStyle = css`
    ::-webkit-scrollbar-thumb {
      background-color: ${theme === 'light' ? color.palette_gray_tint_04 : '#626262'};
    }
    ::-webkit-scrollbar-track {
      background-color: ${theme === 'light' ? color.surface_default_subdued : '#2e2e2e'};
    }
  `;

  return (
    <PhoneMockUp type="content" cssStyle={hideOnMobile}>
      <div css={ClipScrollbarContainer}>
        <article css={[ContentPreviewLayout, ScrollbarStyle]}>
          <Typography as="h2" type="subheading" id="title">
            {name}
          </Typography>
          <section
            className="content-preview"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: description ? DOMPurify.sanitize(description) : '' }}
          />
        </article>
      </div>
    </PhoneMockUp>
  );
}

const ScrollLayout = css`
  overflow-y: auto;
  width: 100%;

  ${onMobile} {
    overflow-y: initial;
  }
`;

const ClipScrollbarContainer = css`
  width: 100%;
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  overflow-x: hidden;
`;

const ContentPreviewLayout = css`
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  height: 100%;

  padding: 0 ${spacing.padding.medium};

  ::-webkit-scrollbar {
    width: 6px;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 6px;
  }
  ::-webkit-scrollbar-track {
    border-radius: 6px;
  }

  > h2 {
    margin: ${spacing.margin.medium} 0 29px;
    text-align: center;
  }

  > section {
    max-width: 100%;
    height: auto;

    font-size: 0.57em;

    * {
      max-width: 100%;
    }
  }
`;

const GroupLayout = css`
  > * {
    width: 293px;
  }

  ${onMobile} {
    flex-direction: column;

    > *:first-of-type {
      margin-bottom: ${spacing.margin.xlarge2};
    }

    > * {
      width: initial;
    }
  }
`;

const helpLink = 'https://rwd.to/realworldstudio-manual-mission';

const textEditorHeight = css`
  .ql-editor {
    min-height: 300px;
  }
`;
